/**
 * ONE BASE URL PER SERVICE, and the service segment lives in that base URL.
 *
 * The deployed edge routes on the FIRST PATH SEGMENT: `/<service>/api/v1/…`
 * reaches that service's upstream and nothing else does. A bundle that built
 * `<origin>/api/v1/…` — the shape this module carried until the develop edge
 * answered PRD-TDD §23 Q8 — matches no location block, so every call the browser
 * made came back **404 from nginx**, including the `authConfiguration` read the
 * sign-in screen renders itself from.
 *
 * Naming the segment was necessary and not sufficient. ONE origin shared by all
 * nine also assumes one host proxies all nine, and on develop no host does: the
 * SPA host proxies nothing and answers a service path with `200 text/html` out
 * of its own history fallback, which a status-code-only probe misreads as
 * success. Nine independent base URLs remove that assumption instead of
 * re-pointing it — each service is addressed where it actually answers, and the
 * production shape, where every SPA host proxies its own API path space, becomes
 * a change of nine values rather than a change of this module. It is also the
 * scheme `frontend-admin` already carries, so the two consoles now differ only
 * in which hosts their values name.
 *
 * The service that OWNS a path is still resolved here rather than restated at
 * every call site. `SERVICE_ROUTES` below is the §10.3 consumption contract
 * restated as code, and it is ordered and **first match wins**, because several
 * families differ only past their second segment: `/contentItems/{id}` is
 * content, `…/access` is finance, `…/comments` is engagement, `…/views` is
 * analytics, and the literal `/contentItems/viewCounts` is analytics rather than
 * a content item whose id happens to read `viewCounts`.
 * `/mediaAssets/{id}/playback` (assets) is a strict prefix of
 * `/mediaAssets/{id}/playbackPosition` (analytics), so every rule is anchored at
 * both ends rather than matched loosely.
 *
 * An unrouted path THROWS. The alternative — falling back to one service —
 * reproduces exactly the defect this module exists to prevent: a call that looks
 * sent, resolves somewhere, and answers wrongly. A caller that knows better than
 * the table names the service itself through `endpoint()`.
 */

/** Dev-only scheme, resolvable ONLY while `import.meta.env.DEV` is true. */
const DEV_MOCK_ORIGIN = 'mock://msw';

export const API_PREFIX = '/api/v1';

export const SERVICE_KEYS = [
  'auth',
  'content',
  'assets',
  'finance',
  'engagement',
  'notification',
  'support',
  'analytics',
  'search',
] as const;

export type ServiceKey = (typeof SERVICE_KEYS)[number];

/**
 * `/warmup` is the one path whose owner is named by the caller rather than by
 * the path, because every service publishes it at the same address. The warm-up
 * module passes `?service=<key>`; the query is read here and the path that
 * reaches the edge keeps it, since the route accepts it.
 */
const WARMUP_PATH = '/warmup';

const SERVICE_ROUTES: readonly { readonly pattern: RegExp; readonly service: ServiceKey }[] = [
  // ── analytics — every literal that would otherwise be read as an id ───────
  { pattern: /^\/contentItems\/viewCounts$/, service: 'analytics' },
  { pattern: /^\/contentItems\/[^/]+\/views$/, service: 'analytics' },
  { pattern: /^\/contentItems\/[^/]+\/creditRow$/, service: 'analytics' },
  { pattern: /^\/mediaAssets\/[^/]+\/playbackPosition$/, service: 'analytics' },
  { pattern: /^\/me\/continueWatching$/, service: 'analytics' },
  { pattern: /^\/me\/playbackPositions$/, service: 'analytics' },
  { pattern: /^\/editorialPosts\/readCounts$/, service: 'analytics' },
  { pattern: /^\/editorialPosts\/[^/]+\/reads$/, service: 'analytics' },

  // ── assets ───────────────────────────────────────────────────────────────
  { pattern: /^\/mediaAssets\/[^/]+\/playback$/, service: 'assets' },
  { pattern: /^\/mediaAssets\/[^/]+\/renditions$/, service: 'assets' },
  { pattern: /^\/contentItems\/[^/]+\/mediaAssets$/, service: 'assets' },
  { pattern: /^\/artworks(:batchResolve)?$/, service: 'assets' },
  { pattern: /^\/documents(\/.*)?$/, service: 'assets' },

  // ── finance ──────────────────────────────────────────────────────────────
  { pattern: /^\/plans(\/.*)?$/, service: 'finance' },
  { pattern: /^\/me\/subscription$/, service: 'finance' },
  // The subscriber's own cancellation. Its own rule rather than a looser
  // `/me/subscription.*`, because a pattern that admitted anything past the
  // segment would route a path this client does not have to a service that
  // does not answer it — and the table's whole job is that an unrouted path
  // cannot be sent at all.
  { pattern: /^\/me\/subscription:cancel$/, service: 'finance' },
  { pattern: /^\/me\/payments$/, service: 'finance' },
  // The payer's code, forwarded against one payment. Its own rule for the same
  // reason `:cancel` has one: a looser `/me/payments.*` would route the reads
  // beneath it that this client does not have.
  { pattern: /^\/me\/payments\/[^/]+:confirmWithCode$/, service: 'finance' },
  // Which checkout step the bound provider requires, read before the write that
  // needs it. Public, like `/plans`.
  { pattern: /^\/paymentMethod$/, service: 'finance' },
  { pattern: /^\/me\/entitlements$/, service: 'finance' },
  { pattern: /^\/contentItems\/[^/]+\/access$/, service: 'finance' },

  // ── engagement ───────────────────────────────────────────────────────────
  { pattern: /^\/contentItems\/[^/]+\/counters$/, service: 'engagement' },
  { pattern: /^\/contentItems\/[^/]+\/comments$/, service: 'engagement' },
  { pattern: /^\/contentItems\/[^/]+\/reaction$/, service: 'engagement' },
  // The SAME three routes over the other subject, and they MUST sit above the
  // `/editorialPosts(/.*)?` rule further down: that rule claims the whole family
  // for `content`, so a comment read left below it was addressed to the service
  // that owns the post rather than the service that owns the thread — a 404
  // from the wrong upstream, not a routing error anything would report.
  { pattern: /^\/editorialPosts\/[^/]+\/counters$/, service: 'engagement' },
  { pattern: /^\/editorialPosts\/[^/]+\/comments$/, service: 'engagement' },
  { pattern: /^\/editorialPosts\/[^/]+\/reaction$/, service: 'engagement' },
  { pattern: /^\/comments\/.+$/, service: 'engagement' },
  { pattern: /^\/reports(\/.*)?$/, service: 'engagement' },

  // ── notification ─────────────────────────────────────────────────────────
  { pattern: /^\/notifications([/:].*)?$/, service: 'notification' },
  { pattern: /^\/channelPreferences(\/.*)?$/, service: 'notification' },

  // ── support ──────────────────────────────────────────────────────────────
  { pattern: /^\/cases(\/.*)?$/, service: 'support' },
  { pattern: /^\/caseTypes$/, service: 'support' },
  { pattern: /^\/caseStates$/, service: 'support' },

  // ── search ───────────────────────────────────────────────────────────────
  { pattern: /^\/results$/, service: 'search' },
  { pattern: /^\/shelfItems(\/.+)?$/, service: 'search' },
  { pattern: /^\/filters$/, service: 'search' },

  // ── auth ─────────────────────────────────────────────────────────────────
  { pattern: /^\/authConfiguration$/, service: 'auth' },
  { pattern: /^\/otps(\/.*)?$/, service: 'auth' },
  { pattern: /^\/sessions([/:].*)?$/, service: 'auth' },
  { pattern: /^\/tokens:refresh$/, service: 'auth' },
  { pattern: /^\/passwords:recover$/, service: 'auth' },
  { pattern: /^\/me$/, service: 'auth' },
  { pattern: /^\/me\/avatar$/, service: 'auth' },
  { pattern: /^\/me\/contactNumber:change$/, service: 'auth' },
  { pattern: /^\/me\/password(:.+)?$/, service: 'auth' },

  // ── content — the remainder, and never a catch-all ────────────────────────
  { pattern: /^\/contentItems(\/.*)?$/, service: 'content' },
  { pattern: /^\/shelves(\/.*)?$/, service: 'content' },
  { pattern: /^\/categories(\/.*)?$/, service: 'content' },
  { pattern: /^\/tagStrip$/, service: 'content' },
  { pattern: /^\/tags(\/.*)?$/, service: 'content' },
  { pattern: /^\/me\/collections$/, service: 'content' },
  { pattern: /^\/collections(\/.*)?$/, service: 'content' },
  { pattern: /^\/profiles(\/.*)?$/, service: 'content' },
  { pattern: /^\/creditHolders(\/.*)?$/, service: 'content' },
  { pattern: /^\/editorialPosts(\/.*)?$/, service: 'content' },
  { pattern: /^\/informationalPages(\/.*)?$/, service: 'content' },
  { pattern: /^\/siteSettings(\/.*)?$/, service: 'content' },
];

/**
 * One variable per service, each carrying its own service segment. Nothing here
 * is derived from anything else: two services behind one host are two identical
 * strings, which is what makes moving ONE of them a one-line configuration
 * change rather than a new branch in this module.
 */
const RAW_BASE_URLS: Record<ServiceKey, string | undefined> = {
  auth: import.meta.env.VITE_AUTH_BASE_URL,
  content: import.meta.env.VITE_CONTENT_BASE_URL,
  assets: import.meta.env.VITE_ASSETS_BASE_URL,
  finance: import.meta.env.VITE_FINANCE_BASE_URL,
  engagement: import.meta.env.VITE_ENGAGEMENT_BASE_URL,
  notification: import.meta.env.VITE_NOTIFICATION_BASE_URL,
  support: import.meta.env.VITE_SUPPORT_BASE_URL,
  analytics: import.meta.env.VITE_ANALYTICS_BASE_URL,
  search: import.meta.env.VITE_SEARCH_BASE_URL,
};

function variableNameFor(service: ServiceKey): string {
  return `VITE_${service.toUpperCase()}_BASE_URL`;
}

/**
 * The base URL for one service, service segment included.
 *
 * Resolved on demand rather than at module load, so one missing value surfaces
 * as a failing request on the one surface that needs it instead of as a blank
 * document at boot.
 */
export function baseUrlFor(service: ServiceKey): string {
  const configured = RAW_BASE_URLS[service]?.trim() ?? '';

  if (configured === DEV_MOCK_ORIGIN) {
    // Guard 2 of frontend-layout.md §10.10: a dev-only base URL THROWS in a
    // production build rather than resolving it. Resolving it silently would
    // ship a build whose requests never leave for a backend that does exist —
    // the failure would surface as an empty product, not as a build error.
    if (!import.meta.env.DEV) {
      throw new Error(
        `${variableNameFor(service)} is "${DEV_MOCK_ORIGIN}" in a production build. ` +
          'The dev mock origin is never a shipped value — set the real base URL and rebuild.',
      );
    }
    // In dev the request is answered by the MSW worker, so the origin only has
    // to be a well-formed absolute URL — but it must still tell the services
    // apart, because production does. The segment is the one production carries.
    return `${window.location.origin}/${service}`;
  }

  if (!configured) {
    if (!import.meta.env.DEV) {
      throw new Error(
        `No base URL is configured for "${service}". Set ${variableNameFor(service)} to the ` +
          'origin that proxies it, service segment included — the edge routes on that segment ' +
          'and matches nothing without it.',
      );
    }
    // Dev and the test ring only, where the worker answers on the bundle's own
    // origin and an unset variable is the normal case rather than a
    // misconfigured deploy. The branch above is what keeps that leniency out of
    // every served build.
    return `${window.location.origin}/${service}`;
  }

  return configured.replace(/\/+$/, '');
}

/** `/plans/01J:subscribe` → `finance`. Throws on a path no rule claims. */
export function serviceForPath(path: string): ServiceKey {
  const withSlash = path.startsWith('/') ? path : `/${path}`;
  const questionMark = withSlash.indexOf('?');
  const bare = questionMark === -1 ? withSlash : withSlash.slice(0, questionMark);
  const query = questionMark === -1 ? '' : withSlash.slice(questionMark + 1);

  if (bare === WARMUP_PATH) {
    const named = new URLSearchParams(query).get('service');
    if (named && (SERVICE_KEYS as readonly string[]).includes(named)) return named as ServiceKey;
    throw new Error(
      `${WARMUP_PATH} needs an explicit ?service=<key>; every service publishes it at the same address.`,
    );
  }

  const matched = SERVICE_ROUTES.find((route) => route.pattern.test(bare));
  if (!matched) {
    throw new Error(
      `No backend owns "${bare}". Add it to SERVICE_ROUTES in @shared/api/config — the edge ` +
        'routes on the service segment, so an unrouted path cannot be sent at all.',
    );
  }

  return matched.service;
}

/**
 * The explicit form: the caller names the owner and the routing table is not
 * consulted. The service segment already sits in the base URL, so this appends
 * the version prefix and the path and nothing else.
 */
export function endpoint(service: ServiceKey, path: string): string {
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrlFor(service)}${API_PREFIX}${suffix}`;
}

/**
 * `/me/subscription` →
 * `https://api.shahedni.7ffdev.ir/finance/api/v1/me/subscription`
 *
 * `service` overrides the routing table for a caller that already knows its
 * owner; without it the owner is read off the path.
 */
export function apiUrl(path: string, service?: ServiceKey): string {
  const withSlash = path.startsWith('/') ? path : `/${path}`;
  return endpoint(service ?? serviceForPath(withSlash), withSlash);
}

export const SITE_ORIGIN = (import.meta.env.VITE_SITE_ORIGIN ?? 'https://shahedni.com').replace(/\/+$/, '');

export const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT ?? 'local';

export const BUILD_SHA = import.meta.env.VITE_BUILD_SHA ?? '';
